import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { authState } from '../store/atoms';
import Sidebar from './Sidebar';
import Header from './Header';
import MobileHeader from './MobileHeader';
import { teacherAPI, userAPI } from '../services';

const Layout: React.FC = () => {
  const auth = useAtomValue(authState);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState({
    lessonRequests: 0,
    oneDayRequests: 0,
    contacts: 0,
    modifications: 0,
    userCount: 0,
  });

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // 선생님이 로그인했을 때 pending 개수 로드
  useEffect(() => {
    if (auth.isAuthenticated && auth.userType === 'teacher') {
      loadPendingCounts();
    }
  }, [auth.isAuthenticated, auth.userType]);

  // 페이지 포커스 시 pending counts 새로고침
  useEffect(() => {
    const handleFocus = () => {
      if (auth.isAuthenticated && auth.userType === 'teacher') {
        teacherAPI.invalidatePendingCountsCache();
        loadPendingCounts();
      }
    };

    const handleRefreshCounts = () => {
      if (auth.isAuthenticated && auth.userType === 'teacher') {
        teacherAPI.invalidatePendingCountsCache();
        loadPendingCounts();
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('refreshPendingCounts', handleRefreshCounts);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('refreshPendingCounts', handleRefreshCounts);
    };
  }, [auth.isAuthenticated, auth.userType]);

  const loadPendingCounts = async () => {
    try {
      const [pendingResponse, usersResponse] = await Promise.all([
        teacherAPI.getPendingCounts(),
        userAPI.getUsers({ status: 'active', limit: 1 })
      ]);

      setPendingCounts({
        lessonRequests: pendingResponse.data?.lessonRequests || 0,
        oneDayRequests: pendingResponse.data?.oneDayRequests || 0,
        contacts: pendingResponse.data?.contacts || 0,
        modifications: pendingResponse.data?.modifications || 0,
        userCount: usersResponse.data?.pagination?.total || 0,
      });
    } catch (error) {
      // 에러 처리
    }
  };

  // 인증된 사용자일 때 사이드바 표시
  const shouldShowSidebar = auth.isAuthenticated;
  // 선생님과 일반 사용자 모두 데스크탑에서 사이드바 표시
  const shouldShowDesktopSidebar = auth.isAuthenticated;

  return (
    <div className="layout">
      {/* 사이드바 - 선생님은 데스크탑에서도 표시, 일반 사용자는 모바일에서만 표시 */}
      {shouldShowSidebar && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          alwaysVisible={shouldShowDesktopSidebar}
          pendingCounts={pendingCounts}
        />
      )}

      <div className={`layout-main ${!shouldShowDesktopSidebar ? 'no-sidebar' : ''}`}>
        {/* 데스크탑 헤더 - 선생님은 데스크탑에서 숨김 */}
        {!shouldShowDesktopSidebar && <Header onMenuClick={toggleSidebar} pendingCounts={pendingCounts} />}

        {/* 모바일 헤더 - 모바일에서만 표시 */}
        {shouldShowSidebar && <MobileHeader onMenuClick={toggleSidebar} />}

        <main className="layout-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 